import React, {useRef} from "react";
import {shallowEqual} from "react-redux";
import Folder, {FolderTypeEnum} from "../../domain/Folder";
import "./FolderTreeView.scss";
import {FolderActions} from "../../actions/FolderActions";
import classNames from "classnames";
import {FolderLocation} from "../../locations/FolderLocation";
import {Collapse} from "@mui/material";
import ExpandMore from '@mui/icons-material/ExpandMore';
import {canCreateSubFolder, FolderContextMenu} from './FolderContextMenu';
import {EditFolderDialog} from './EditFolderDialog';
import {EmailActions} from '../../actions/EmailActions';
import {SystemActions} from "../../actions/SystemActions";
import {DB} from "../../db/DbManager";
import {isSpecialFolder} from "../../reducers/SyncHelper";
import {useAppDispatch, useAppSelector, useAppStore} from "../../hooks/ReduxHooks";
import {getFolderIcon, isValidFolderForMovingEmail} from "../../util/FolderUtil";
import {DLQFolderId, GreylistFolderId} from "../../reducers/FolderReducer";

interface DisplayFolder extends Folder {
    children: DisplayFolder[];
    childUnreadCount: number;
}

function constructFolderTree(folders: Folder[], draftEmailCount: number): DisplayFolder[] {
    const root: DisplayFolder[] = [];
    const folderMap = new Map<string, DisplayFolder>();

    for (const folder of folders) {
        folderMap.set(folder.serverId,
            {
                ...folder,
                children: [],
                unreadCount: folder.type === FolderTypeEnum.Drafts ? draftEmailCount : folder.unreadCount,
                childUnreadCount: 0,
            });
    }

    folderMap.forEach(entry => {
        const folder = folderMap.get(entry.parentId);
        if (folder) {
            folder.children.push(entry);
            folder.childUnreadCount += entry.unreadCount + entry.childUnreadCount;
        } else {
            root.push(entry);
        }
    });

    sortFolderTree(root, 0);

    return root;
}

function sortFolderTree(folders: DisplayFolder[], level: number) {
    folders.sort((a, b) => a.name > b.name ? 1 : -1);

    for (const folder of folders) {
        sortFolderTree(folder.children, level + 1);
        folder.indent = level;
    }
}

export const FolderTreeView = () => {

    const menuRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const store = useAppStore();

    const folderList = useAppSelector(s => s.folderState.folderList.filter(f => !isSpecialFolder(f)), shallowEqual);
    const outgoingEmails = useAppSelector(s => s.folderState.outgoingEmails);
    const expandedFolderIds = useAppSelector(s => s.folderState.expandedFolderIds);
    const selectedFolderId = useAppSelector(s => s.folderState.selectedFolderId);
    const folderSelectionMode = useAppSelector(s => s.folderState.folderSelectionMode);
    const contextFolder = useAppSelector(s => s.folderState.contextFolder);
    const editingFolder = useAppSelector(s => s.folderState.editingFolder);
    const emailsToMove = useAppSelector(s => s.folderState.emailsToMove);
    const draftEmailCount = useAppSelector(s => s.itemState.draftEmailHeaders.length);

    const folderTree = constructFolderTree(folderList, draftEmailCount);

    const isPrivateBrowserMode = DB.isPrivateBrowserMode();

    const onExpandFolder = (folder: Folder, expand: boolean) => FolderActions.expandFolder(dispatch, store.getState, folder.folderId, expand);
    const onSetContextFolder = (folder: Folder) => dispatch(FolderActions.setContextFolder(folder));
    const onClearContextFolder = () => dispatch(FolderActions.setContextFolder(undefined));

    function showFolder(folder: DisplayFolder) {
        return !folder.hidden;
    }

    function isFolderSyncedOrValidTarget(folder: DisplayFolder) {
        if (folderSelectionMode === "email") {
            return isValidFolderForMovingEmail(folder) && folder.folderId !== selectedFolderId;
        }
        if (folderSelectionMode === "folder") {
            return canCreateSubFolder(folder.type) && folder.serverId !== editingFolder.serverId;
        }
        return true;
    }

    async function handleSelectFolder(e: any, folder: DisplayFolder) {
        if (folderSelectionMode === "email") {
            if (isFolderSyncedOrValidTarget(folder)) {
                dispatch(FolderActions.cancelMoveFolder());
                EmailActions.completeMoveEmails(dispatch, emailsToMove, folder);
            }
        } else if (folderSelectionMode === "folder") {
            if (isFolderSyncedOrValidTarget(folder)) {
                dispatch(FolderActions.cancelMoveFolder());
                FolderActions.completeMoveFolder(dispatch, editingFolder, folder.serverId);
            }
        } else {
            FolderLocation.selectFolder(folder);
        }
        dispatch(SystemActions.showFolderMenu(false));
        e.stopPropagation();
    }

    function createFolderDiv(folder: DisplayFolder) {
        const expanded = expandedFolderIds.includes(folder.folderId);

        const childFolderElements = folder.children.length > 0
            ? (
                <Collapse in={expanded} timeout="auto">
                    {folder.children
                        .filter(showFolder)
                        .map(child => createFolderDiv(child))
                    }
                </Collapse>)
            : null;

        const folderClasses = classNames("folder", "selectable", "pointer",
            {
                "selected": folder.folderId === selectedFolderId,
                "synced": isFolderSyncedOrValidTarget(folder) || isPrivateBrowserMode,
                "context-selected": folder.folderId === contextFolder?.folderId
            },
        );

        const onExpand = (e: any) => {
            onExpandFolder(folder, !expanded);
            e.stopPropagation();
        };

        const folderCountBadge =
            folder.type === FolderTypeEnum.Outbox
                ? outgoingEmails.length
                : folder.unreadCount;

        const unreadClasses = classNames("unread-count", {
            "dlq-entries": folder.folderId === DLQFolderId,
            "greylisted-entries": folder.folderId === GreylistFolderId,
            "warning-entries": folder.type === FolderTypeEnum.Drafts || folder.type === FolderTypeEnum.Outbox,
        });

        return (
            <div className={classNames("folder-item", {"nav": folder.indent === 0})}
                 key={folder.folderId}
                 onClick={(e: any) => handleSelectFolder(e, folder)}
            >
                <div className={classNames(folderClasses)}
                     id={"folder-" + folder.folderId}
                     onContextMenu={() => onSetContextFolder(folder)}
                     style={{paddingLeft: ((folder.indent + 1) * 16) + "px", transition: "none"}}>
                    <span className="folder-type">
                         {getFolderIcon(folder.icon)}
                    </span>

                    <span className="folder-name">{folder.name}</span>
                    {expanded && <span className="unread-count">{folderCountBadge || null}</span>}
                    {!expanded && <span className={unreadClasses}>{(folderCountBadge + folder.childUnreadCount) || null}</span>}

                    <span style={{flex: "1 1 auto"}}>&nbsp;</span>

                    {folder.children.length > 0 && (
                        <div className={classNames("selectable-2 expander-no-padding")}>
                            <ExpandMore onClick={onExpand} className={classNames({"expanded": expanded})}/>
                        </div>
                    )}
                </div>
                {childFolderElements}
            </div>
        );
    }

    return (
        <>
            <div className="folder-tree" ref={menuRef}>
                {folderTree
                    .filter(showFolder)
                    .map(createFolderDiv)
                }
            </div>
            <FolderContextMenu elementRef={menuRef} onClose={() => onClearContextFolder()}/>

            <EditFolderDialog/>
        </>
    );
};
